<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import LeFloatingContainer from "@dsk/components/LeFloatingContainer.vue";
import { ref } from "vue";
import { RouterLink } from "vue-router";

import { MatomoHelper } from "@/helpers";
import { type BusinessSubscriptionBox, type BusinessSubscriptionModal, MatomoEventCategory } from "@/types";

import LeBusinessSubscriptionModals from "./LeBusinessSubscriptionModals.vue";
import LeUspItem from "./LeUspItem.vue";

const props = defineProps<{ subscription: BusinessSubscriptionBox }>();

const modal = ref<BusinessSubscriptionModal | undefined>();

const onCtaButtonClick = () => {
  modal.value = "request";

  const action = MatomoHelper.getBusinessSubscriptionAction(props.subscription.type);
  if (!action) {
    return;
  }

  let label = "Clicked button ";
  switch (props.subscription.type) {
    case "individual":
      label += '"Try free 7 days"';
      break;
    case "flexible":
      label += '"Try free 30 days"';
      break;
    case "professional":
      label += '"More information"';
      break;
  }

  MatomoHelper.sendEvent(MatomoEventCategory.SubscriptionPageBusiness, action, label);
};
</script>

<template>
  <LeFloatingContainer
    class="le-business-subscription-box"
    :color="subscription.color"
    :header-color="subscription.headerColor"
  >
    <template v-if="subscription.header" #header>
      {{ subscription.header }}
    </template>

    <div
      class="le-business-subscription-box__content"
      :class="{ 'le-business-subscription-box__content--with-header': subscription.header }"
    >
      <div class="le-business-subscription-box__top">
        <LeHeading class="le-business-subscription-box__title" tag="h2" size="l">{{ subscription.title }}</LeHeading>
        <LeLabel size="l" :color="subscription.label.color">{{ subscription.label.text }}</LeLabel>
        <LeButton
          class="le-business-subscription-box__cta-button"
          size="large"
          :variant="subscription.button.variant"
          @click="onCtaButtonClick()"
        >
          {{ subscription.button.text }}
        </LeButton>
      </div>
      <ul class="list-unstyled mb-0">
        <LeUspItem v-for="(usp, index) in subscription.usps" :key="index">
          <template v-if="usp.route">
            <RouterLink class="le-business-subscription-box__link" :to="{ name: `${usp.route}` }">
              {{ usp.text }}
            </RouterLink>
          </template>
          <template v-else>{{ usp.text }}</template>
        </LeUspItem>
      </ul>
    </div>

    <LeBusinessSubscriptionModals v-model="modal" :subscription-type="subscription.type" />
  </LeFloatingContainer>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-business-subscription-box {
  &__content {
    padding: 44px 30px;

    &--with-header {
      padding-top: 12px;
    }
  }

  &__top {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 48px;
    border-bottom: 1px solid @colors[@secondary][600];
    text-align: center;
  }

  &__title {
    margin-bottom: 6px;
  }

  &__cta-button {
    margin-top: 40px;
  }

  &__link {
    .body-text("s");
    text-decoration: underline;
  }
}
</style>
